import { HttpClient } from '@angular/common/http';
import {
  FileDoesNotExistError,
  FolderDoesNotExistError,
  FullVirtualDirectoryLike,
  VirtualFileLike,
} from '@rxap/fs';
import { catchError, firstValueFrom, map, of, throwError, timeout } from 'rxjs';
import { HttpFile } from './http-file';

export function isHttpDirectory(
  value: HttpDirectory | HttpFile | undefined
): value is HttpDirectory {
  return !!value && value instanceof HttpDirectory;
}

export class HttpDirectory implements FullVirtualDirectoryLike {

  constructor(
    public readonly baseUri: string,
    public readonly http: HttpClient,
    public readonly name: string,
    public readonly fullName = '',
    protected readonly children = new Map<string, HttpFile | HttpDirectory>()
  ) {}

  get childrenNames() {
    return Array.from(this.children.keys());
  }

  forEachFile(callback: (file: HttpFile) => void): void {
    for (const child of this.children.values()) {
      if (child instanceof HttpFile) {
        callback(child);
      } else {
        child.forEachFile(callback);
      }
    }
  }

  *iterateEachFile(): IterableIterator<HttpFile> {
    for (const child of this.children.values()) {
      if (child instanceof HttpFile) {
        yield child;
      } else {
        yield * child.iterateEachFile();
      }
    }
  }

  addFile(file: HttpFile, force?: boolean): Promise<HttpFile> {
    throw new Error('Not implemented (HttpDirectory::addFile)');
  }

  registerFile(path: string, mimetype?: string, timeoutMs = 1000 * 60) {
    path = path.startsWith('/') ? path.substring(1) : path;
    const fragments = path.split('/');
    const fileName = fragments.pop();
    if (!fileName) {
      throw new Error(`The file path '${path}' is invalid`);
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let directory: HttpDirectory = this;
    while (fragments.length) {
      const fragment = fragments.shift();
      if (!fragment) {
        throw new Error(`The file path '${path}' is invalid`);
      }

      if (directory.hasDirectory(fragment)) {
        directory = directory.directory(fragment);
      } else {
        const child = new HttpDirectory(
          this.baseUri,
          this.http,
          fragment,
          `${directory.fullName}/${fragment}`
        );
        directory.children.set(fragment, child);
        directory = child;
      }
    }
    const url = `${this.baseUri}/${path}`;
    const child = new HttpFile(
      fileName,
      `${directory.fullName}/${fileName}`,
      () =>
        firstValueFrom(
          this.http.get(url, { responseType: 'arraybuffer', withCredentials: false }).pipe(
            timeout({
              first: timeoutMs,
              with: () =>
                throwError(
                  () =>
                    new Error(
                      `The timeout '${timeoutMs}'ms for the request '${url}'`
                    )
                ),
            })
          )
        ),
      () =>
        firstValueFrom(
          this.http.head(url, { observe: 'response', withCredentials: false }).pipe(
            map((response) => response.ok),
            catchError(() => of(false)),
            timeout({
              first: timeoutMs,
              with: () =>
                throwError(
                  () =>
                    new Error(
                      `The timeout '${timeoutMs}'ms for the request '${url}'`
                    )
                ),
            })
          )
        )
    );
    if (mimetype) {
      child.setMimeType(mimetype);
    }
    directory.setFile(fileName, child);
  }

  public setFile(name: string, file: HttpFile) {
    this.children.set(name, file);
  }

  public setDirectory(name: string, directory: HttpDirectory) {
    this.children.set(name, directory);
  }

  clone(name: string = this.name, fullName: string = this.fullName): HttpDirectory {
    if (name !== this.name && !fullName.endsWith(name)) {
      fullName.replace(new RegExp(`${this.name}$`), name);
    }
    return new HttpDirectory(this.baseUri, this.http, name, fullName, new Map(this.children));
  }

  public removeDirectory(name: string) {
    return this.children.has(name) && isHttpDirectory(this.children.get(name)) && this.children.delete(name);
  }

  hasFile(match: (file: VirtualFileLike) => boolean): boolean;
  hasFile(path: string): boolean;
  hasFile(pathOrMatch: string | ((file: VirtualFileLike) => boolean)): boolean {
    if (typeof pathOrMatch === 'string') {
      return this.hasFileByPath(pathOrMatch);
    } else {
      return this.hasFileByMatch(pathOrMatch);
    }
  }

  hasFileByMatch(match: (file: VirtualFileLike) => boolean): boolean {
    return Array.from(this.children.values())
      .filter((child) => child instanceof HttpFile)
      .some((child) => match(child));
  }

  hasFileByPath(path: string): boolean {
    return (
      this.children.has(path) && this.children.get(path) instanceof HttpFile
    );
  }

  removeFile(match: (file: VirtualFileLike) => boolean): boolean;
  removeFile(path: string): boolean;
  removeFile(pathOrMatch: string | ((file: VirtualFileLike) => boolean)): boolean {
    if (typeof pathOrMatch === 'string') {
      return this.removeFileByPath(pathOrMatch);
    } else {
      return this.removeFileByMatch(pathOrMatch);
    }
  }

  removeFileByMatch(match: (file: VirtualFileLike) => boolean): boolean {
    for (const child of this.children.values()) {
      if (child instanceof HttpDirectory) {
        child.removeFile(match);
      } else {
        if (match(child)) {
          this.children.delete(child.name);
          return true;
        }
      }
    }
    return false;
  }

  removeFileByPath(path: string): boolean {
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    const fragments = path.split('/');
    if (fragments.length === 0) {
      return false;
    }
    const first = fragments.shift()!;
    if (fragments.length === 0) {
      if (this.hasFile(first)) {
        this.children.delete(first);
        return true;
      }
    } else {
      if (this.hasDirectory(first)) {
        return this.directory(first).removeFile(fragments.join('/'));
      }
    }
    return false;
  }

  public hasDirectory(name: string) {
    return (
      this.children.has(name) &&
      this.children.get(name) instanceof HttpDirectory
    );
  }

  public file(name: string) {
    const file = this.children.get(name);
    if (file instanceof HttpFile) {
      return file;
    }
    if (!file) {
      throw new FileDoesNotExistError(name);
    }
    throw new Error(`'${name}' is not a file`);
  }

  public directory(name: string) {
    const directory = this.children.get(name);
    if (directory instanceof HttpDirectory) {
      return directory;
    }
    if (!directory) {
      throw new FolderDoesNotExistError(name);
    }
    throw new Error(`'${name}' is not a directory`);
  }

  public findFile(match: (file: HttpFile) => boolean, mimetype?: string): HttpFile;
  public findFile(path: string, mimetype?: string): HttpFile;
  public findFile(pathOrMatch: string | ((file: HttpFile) => boolean), mimetype?: string): HttpFile {
    if (typeof pathOrMatch === 'string') {
      return this.findFileByPath(pathOrMatch, mimetype);
    }
    return this.findFileByMatch(pathOrMatch);
  }

  protected findFileByPath(path: string, mimetype?: string): HttpFile {
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    const fragments = path.split('/');
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let directory: HttpDirectory = this;
    for (let i = 0; i < fragments.length; i++) {
      const fragment = fragments[i];
      if (i === fragments.length - 1) {
        const file = directory.file(fragment);
        if (mimetype) {
          file.setMimeType(mimetype);
        }
        return file;
      }
      directory = directory.directory(fragment);
    }
    throw new Error(`The file '${path}' does not exist`);
  }

  protected findFileByMatch(match: (file: HttpFile) => boolean): HttpFile {
    for (const child of this.flatten()) {
      if (match(child)) {
        return child;
      }
    }
    throw new Error(`Match function does not match any file`);
  }

  public flatten(): HttpFile[] {
    const files: HttpFile[] = [];
    for (const child of this.children.values()) {
      if (child instanceof HttpFile) {
        files.push(child);
      } else {
        files.push(...child.flatten());
      }
    }
    return files;
  }

  toJSON() {
    const json: Record<string, unknown> = {};
    for (const [name, child] of this.children.entries()) {
      if (child instanceof HttpFile) {
        json[name] = null;
      } else {
        json[name] = child.toJSON();
      }
    }
    return json;
  }
}
